import React from 'react';
import PropTypes from 'prop-types';
import moment from 'moment-timezone';
import Popover from '@mui/material/Popover';

import Strings from '../../Strings';
import { DATE_MONTH_DAY_YEAR, DATE_TIME_SPACE_12_UPPERCASE } from '../../constants';
import { CONSENT_DATE_FORMAT, CONSENT_GIVEN_BY, CONSENT_STATUS } from '../../utils/consentTemplates';
import './Consent.scss';

const consentShape = PropTypes.shape({
  status: PropTypes.string,
  obtainedAt: PropTypes.string,
  obtainedByName: PropTypes.string,
  givenBy: PropTypes.string,
  recordedAt: PropTypes.string,
  recordedByName: PropTypes.string,
  template: PropTypes.shape({ id: PropTypes.string, version: PropTypes.number }),
  items: PropTypes.arrayOf(PropTypes.shape({ key: PropTypes.string, text: PropTypes.string, confirmed: PropTypes.bool })),
});

function Separator() {
  return <span className="consent-sep">·</span>;
}

function ConsentDetails({ consent }) {
  const items = consent.items || [];
  const confirmed = items.filter(item => item.confirmed).length;
  return (
    <div className="consent-details" data-testid="consent-details">
      <dl>
        <dt>{Strings.consentChecklist.recorded}</dt>
        <dd>
          {consent.recordedByName
            ? Strings.formatString(Strings.consentChecklist.recordedAtBy, moment(consent.recordedAt).format(DATE_TIME_SPACE_12_UPPERCASE), consent.recordedByName)
            : moment(consent.recordedAt).format(DATE_TIME_SPACE_12_UPPERCASE)}
        </dd>
        <dt>{Strings.consentChecklist.template}</dt>
        <dd>{`${consent.template.id} v${consent.template.version}`}</dd>
      </dl>
      <div className="consent-items">
        <h4>{Strings.formatString(Strings.consentChecklist.informedThatCount, confirmed, items.length)}</h4>
        <ul>
          {items.map(item => (
            <li key={item.key}>
              <span className={`consent-item-icon ${item.confirmed ? 'confirmed' : 'missing'}`} aria-hidden="true" />
              <span>{item.text}</span>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}

ConsentDetails.propTypes = {
  consent: consentShape.isRequired,
};

// detailsInPopover: for fixed-height containers (dashboard tiles) that would clip the expanded details.
export default function ConsentInfo({ enrollment, onRecordConsent, detailsInPopover }) {
  const [expanded, setExpanded] = React.useState(false);
  const [popoverAnchor, setPopoverAnchor] = React.useState(null);
  const consent = enrollment?.consent;

  if (consent) {
    const isWritten = consent.status === CONSENT_STATUS.written;
    const detailsShown = detailsInPopover ? !!popoverAnchor : expanded;
    return (
      <div className="consent-info" data-testid="consent-info">
        <div className="consent-summary">
          <span className="consent-pill ok">{isWritten ? Strings.consentChecklist.pillWritten : Strings.consentChecklist.pillVerbal}</span>
          <span>{moment(consent.obtainedAt, CONSENT_DATE_FORMAT).format(DATE_MONTH_DAY_YEAR)}</span>
          <Separator />
          <span>{consent.givenBy === CONSENT_GIVEN_BY.caregiver ? Strings.consentChecklist.givenByCaregiver : Strings.consentChecklist.givenByPatient}</span>
          {consent.obtainedByName && (
            <>
              <Separator />
              <span>{Strings.formatString(Strings.consentChecklist.obtainedByName, consent.obtainedByName)}</span>
            </>
          )}
          <span className="consent-toggle">
            <button
              type="button"
              className="consent-link"
              aria-expanded={detailsShown}
              onClick={e => (detailsInPopover ? setPopoverAnchor(e.currentTarget) : setExpanded(v => !v))}
              data-testid="consent-details-toggle"
            >
              {detailsShown ? Strings.consentChecklist.hide : Strings.consentChecklist.details}
            </button>
          </span>
        </div>
        {detailsInPopover ? (
          <Popover
            open={!!popoverAnchor}
            anchorEl={popoverAnchor}
            onClose={() => setPopoverAnchor(null)}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
            transformOrigin={{ vertical: 'top', horizontal: 'left' }}
          >
            {/* The popover renders in document.body, so it needs its own .consent-info scope for the styles. */}
            <div className="consent-info consent-details-panel">
              <ConsentDetails consent={consent} />
            </div>
          </Popover>
        ) : (
          expanded && <ConsentDetails consent={consent} />
        )}
      </div>
    );
  }

  if (enrollment?.verbalConsent || enrollment?.writtenConsent) {
    return (
      <div className="consent-info" data-testid="consent-info">
        <div className="consent-summary">
          <span className="consent-pill ok">{enrollment.writtenConsent ? Strings.consentChecklist.pillWritten : Strings.consentChecklist.pillVerbal}</span>
          {enrollment.verifiedAt && (
            <>
              <span>{moment(enrollment.verifiedAt).format(DATE_MONTH_DAY_YEAR)}</span>
              <Separator />
            </>
          )}
          <span className="consent-muted">{Strings.consentChecklist.recordedBeforeChecklist}</span>
        </div>
      </div>
    );
  }

  return (
    <div className="consent-info" data-testid="consent-info">
      <div className="consent-summary">
        <span className="consent-pill warn">{Strings.consentChecklist.pillNotObtained}</span>
        <span className="consent-muted">{Strings.consentChecklist.requiredBeforeActivation}</span>
        {onRecordConsent && (
          <span className="consent-toggle">
            <button type="button" className="consent-link" onClick={onRecordConsent} data-testid="consent-record">
              {Strings.consentChecklist.recordConsent}
            </button>
          </span>
        )}
      </div>
    </div>
  );
}

ConsentInfo.propTypes = {
  enrollment: PropTypes.shape({
    consent: consentShape,
    verbalConsent: PropTypes.bool,
    writtenConsent: PropTypes.bool,
    verifiedAt: PropTypes.string,
  }),
  onRecordConsent: PropTypes.func,
  detailsInPopover: PropTypes.bool,
};
